import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { auth } from '../firebase';
import { signInWithCustomToken } from 'firebase/auth';

// Firebase Functions URLs
const getWebAuthnBaseUrl = () => {
  // Im Development-Modus auf lokale Emulator-URLs zeigen
  if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
    return 'http://localhost:5001/cards-against-humanity-o-67272/us-central1';
  }
  // Produktion - deine Firebase Hosting Domain
  return 'https://us-central1-cards-against-humanity-o-67272.cloudfunctions.net';
};

const WEBAUTHN_BASE_URL = getWebAuthnBaseUrl();

export interface PasskeyAuthResult {
  success: boolean;
  uid?: string;
  error?: string;
}

// Registriere einen neuen Passkey
export async function registerPasskey(): Promise<PasskeyAuthResult> {
  try {
    // 1. Hole Challenge vom Server
    const beginResponse = await fetch(`${WEBAUTHN_BASE_URL}/webauthnRegisterBegin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        username: `admin_${Date.now()}`,
        displayName: 'Admin User'
      })
    });

    if (!beginResponse.ok) {
      throw new Error('Failed to get registration challenge');
    }

    const options = await beginResponse.json();

    // 2. Führe WebAuthn-Registrierung durch
    const credential = await startRegistration(options);

    // 3. Verifiziere mit Server - sende auch die userId
    const finishResponse = await fetch(`${WEBAUTHN_BASE_URL}/webauthnRegisterFinish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credential,
        userId: options.userId // Von der begin response
      })
    });

    if (!finishResponse.ok) {
      throw new Error('Failed to verify registration');
    }

    const { verified, customToken, uid } = await finishResponse.json();

    if (verified && customToken) {
      // 4. Melde dich mit Firebase Custom Token an
      await signInWithCustomToken(auth, customToken);
      
      return {
        success: true,
        uid
      };
    }

    return {
      success: false,
      error: 'Registration verification failed'
    };

  } catch (error: any) {
    console.error('Passkey registration error:', error);
    return {
      success: false,
      error: error.message || 'Passkey-Registrierung fehlgeschlagen'
    };
  }
}

// Anmelden mit existierendem Passkey
export async function loginWithPasskey(uid?: string): Promise<PasskeyAuthResult> {
  try {
    // 1. Hole Challenge vom Server
    const beginResponse = await fetch(`${WEBAUTHN_BASE_URL}/webauthnLoginBegin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid })
    });

    if (!beginResponse.ok) {
      throw new Error('Failed to get login challenge');
    }

    const options = await beginResponse.json();

    // 2. Führe WebAuthn-Authentifizierung durch
    const credential = await startAuthentication(options);

    // 3. Verifiziere mit Server
    const finishResponse = await fetch(`${WEBAUTHN_BASE_URL}/webauthnLoginFinish`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credential,
        challengeKey: options.challengeKey // Von der begin response
      })
    });

    if (!finishResponse.ok) {
      throw new Error('Failed to verify login');
    }

    const { verified, customToken, uid: verifiedUid } = await finishResponse.json();

    if (verified && customToken) {
      // 4. Melde dich mit Firebase Custom Token an
      await signInWithCustomToken(auth, customToken);
      
      return {
        success: true,
        uid: verifiedUid
      };
    }

    return {
      success: false,
      error: 'Login verification failed'
    };

  } catch (error: any) {
    console.error('Passkey login error:', error);
    return {
      success: false,
      error: error.message || 'Passkey-Anmeldung fehlgeschlagen'
    };
  }
}

// Prüfe ob WebAuthn unterstützt wird
export function isWebAuthnSupported(): boolean {
  return typeof window !== 'undefined' && 
         'PublicKeyCredential' in window &&
         typeof window.PublicKeyCredential === 'function';
} 